import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Trash, ArrowRight, Sparkle, Briefcase, MapTrifold, GraduationCap, DiceFive as Dice, ArrowsClockwise, House, Baby, Code, Laptop, Fire, PawPrint, Rocket, Scales, Robot } from '@phosphor-icons/react'
import { useStore } from '../store'
import { TEMPLATES } from '../templates'
import { LOCALES, LOCALE_CALIBRATION, useLocale } from '../locale'
import { cn, daysAgo } from '../lib/utils'
import { deleteUserDecision } from '../api'
import { useAuth } from '../auth'
import { analytics } from '../lib/analytics'
import StreaksCard from '../components/StreaksCard'
import RerollHint from '../components/RerollHint'
import CheckinsSection from '../components/CheckinsSection'
import { buildRebrandSeed, buildTemplateSeed, buildQuitWealthSeed, buildHomepageExample, HOMEPAGE_EXAMPLE } from '../lib/explainerSeeds'
import WarmStartModal, { type WarmStartSeed } from '../components/WarmStartModal'
import { toast } from '../components/Toast'
import DecisionIntakeChat from '../components/DecisionIntakeChat'
import TourLauncher from '../components/Tour/TourLauncher'
import { useTourStore } from '../components/Tour/useTourStore'

const REROLL_AGE_DAYS = 7

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { decisions, addDecision, deleteDecision, results } = useStore()
  const auth = useAuth()
  const seedHandled = useRef(false)

  // Hidden seed handler: /app?seed=rebrand pre-loads the "Should I rebrand LifeOdds?"
  // decision and navigates straight into the builder. Idempotent within a session.
  useEffect(() => {
    // Attribution: the visitor arrived from the Should-I-Quit forerunner.
    // Flag it so a subsequent simulation run counts as a forerunner activation.
    if (searchParams.get('from') === 'siq') {
      try { sessionStorage.setItem('lo-from-siq', '1') } catch { /* ignore */ }
      analytics.track('app_opened_from_siq')
    }
    if (seedHandled.current) return
    const seed = searchParams.get('seed')
    if (seed === 'rebrand') {
      seedHandled.current = true
      const decision = buildRebrandSeed()
      const id = addDecision(decision)
      queueMicrotask(() => {
        toast({
          kind: 'success',
          title: 'Rebrand decision seeded',
          message: '5 finalist names loaded. Tweak the numbers, then run the simulation.',
          duration: 6500,
        })
      })
      setSearchParams({}, { replace: true })
      navigate(`/decision/${id}`, { replace: true })
    } else if (seed === 'job-change') {
      seedHandled.current = true
      const decision = buildTemplateSeed('should-i-take-the-new-job')
      const id = addDecision(decision)
      queueMicrotask(() => {
        toast({
          kind: 'success',
          title: 'Job decision pre-loaded',
          message: 'Tweak your salary, layoff risk, and satisfaction — then run 1,000 futures.',
          duration: 6500,
        })
      })
      setSearchParams({}, { replace: true })
      navigate(`/decision/${id}`, { replace: true })
    } else if (seed === HOMEPAGE_EXAMPLE.key) {
      // The homepage's sourced example, opened in the builder to re-run.
      seedHandled.current = true
      const id = addDecision(buildHomepageExample())
      queueMicrotask(() => {
        toast({
          kind: 'success',
          title: "Master's example loaded",
          message: 'Sourced numbers (StepStone, German semester fees). Put in your own salary, then run it.',
          duration: 6500,
        })
      })
      setSearchParams({}, { replace: true })
      navigate(`/decision/${id}`, { replace: true })
    } else if (seed === 'quit-wealth') {
      // The "future me" hand-off: a long-horizon wealth-mode decision that
      // projects net worth + earliest retirement, carrying the tool's growth
      // pace + feel level so it continues the user's scenario.
      seedHandled.current = true
      const growthIdx = Number(searchParams.get('growth'))
      const feelIdx = Number(searchParams.get('feel'))
      const decision = buildQuitWealthSeed({
        growthIdx: Number.isFinite(growthIdx) ? growthIdx : undefined,
        feelIdx: Number.isFinite(feelIdx) ? feelIdx : undefined,
      })
      const id = addDecision(decision)
      queueMicrotask(() => {
        toast({
          kind: 'success',
          title: 'Long-run view loaded',
          message: 'Set your take-home pay, expenses, and savings — then see your net worth and earliest retirement.',
          duration: 7000,
        })
      })
      setSearchParams({}, { replace: true })
      navigate(`/decision/${id}`, { replace: true })
    }
  }, [searchParams, addDecision, navigate, setSearchParams])
  const [showNew, setShowNew] = useState(false)
  const [showIntake, setShowIntake] = useState(false)
  // Arriving from the Decision Coach with context — open the AI intake
  // pre-seeded so the numeric model starts from what the coach learned.
  const location = useLocation()
  const [intakeSeed, setIntakeSeed] = useState<string | null>(null)
  useEffect(() => {
    const seed = (location.state as { intakeSeed?: string } | null)?.intakeSeed
    if (seed) {
      setIntakeSeed(seed)
      setShowIntake(true)
      window.history.replaceState({}, '')
    }
  }, [location.state])
  const [draft, setDraft] = useState({ name: '', horizon: 5 })
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const tourCompleted = useTourStore((s) => s.completed)
  const showTourLauncher = auth.user && !tourCompleted && decisions.length === 0

  const removeDecision = (id: string) => {
    deleteDecision(id)
    if (auth.user) {
      // best-effort server cleanup so the next sync doesn't resurrect it
      void deleteUserDecision(id)
    }
  }

  const create = () => {
    if (!draft.name.trim()) return
    const id = addDecision({
      name: draft.name,
      horizon_years: draft.horizon,
      discount_rate: 0.05,
      risk_aversion: 0.002,
    })
    setShowNew(false)
    setDraft({ name: '', horizon: 5 })
    navigate(`/decision/${id}`)
  }

  const useTemplate = (key: string) => {
    const t = TEMPLATES.find((t) => t.key === key)!
    // Scale EUR-baseline numeric variables to the user's locale-native magnitudes.
    const localeCode = useLocale.getState().code
    const factor = LOCALE_CALIBRATION[localeCode] || 1
    const localeCurrency = LOCALES[localeCode].currency

    const isCurrencyRole = (role: string) =>
      role === 'income' || role === 'cost' || role === 'one_time_income' || role === 'one_time_cost'
    const looksLikeCurrencyUnit = (u?: string) => !!u && /€|EUR|\/year|\/month/i.test(u) && !/pts|score|prob|chance/i.test(u)

    // Hand the localized draft to the warm-start modal: confirm the 1-2
    // numbers that matter → simulate straight to Results (or open the builder).
    setWarmSeed({
      name: t.label,
      horizon_years: t.horizon_years,
      discount_rate: 0.05,
      risk_aversion: 0.002,
      base_currency: localeCurrency,
      choices: t.choices.map((c) => ({
        id: crypto.randomUUID(),
        name: c.name,
        notes: c.notes,
        variables: c.variables.map((v) => {
          if (factor === 1 || !v.dist?.params) return v
          const params = v.dist.params
          const isScalable =
            isCurrencyRole(v.role) ||
            (looksLikeCurrencyUnit(v.units) && v.dist.kind !== 'Bernoulli')
          if (!isScalable) return v
          const newParams: Record<string, number> = { ...params }
          // LogNormal params are in log space: scaling the amount shifts mu by ln(factor).
          if (v.dist.kind === 'LogNormal') {
            if (typeof newParams.mu === 'number') newParams.mu = newParams.mu + Math.log(factor)
            return { ...v, dist: { ...v.dist, params: newParams } }
          }
          for (const k of ['mu', 'sigma']) {
            if (typeof newParams[k] === 'number') {
              newParams[k] = newParams[k] * factor
            }
          }
          return { ...v, dist: { ...v.dist, params: newParams } }
        }),
      })),
    })
  }

  const templateIcon = (key: string) => {
    if (key === 'job') return Briefcase
    if (key === 'move') return MapTrifold
    if (key === 'masters') return GraduationCap
    if (key === 'startup') return Rocket
    if (key === 'buy-vs-rent') return House
    if (key === 'kids') return Baby
    if (key === 'bootcamp') return Code
    if (key === 'remote') return Laptop
    if (key === 'fire') return Fire
    if (key === 'pet') return PawPrint
    return Sparkle
  }

  const CATEGORY_LABELS: Record<string, { label: string; emoji: string }> = {
    all:       { label: 'All',       emoji: '✨' },
    careers:   { label: 'Career',    emoji: '💼' },
    money:     { label: 'Money',     emoji: '💵' },
    moves:     { label: 'Moves',     emoji: '🗺️' },
    education: { label: 'Education', emoji: '🎓' },
    life:      { label: 'Life',      emoji: '🌱' },
  }
  const [templateCategory, setTemplateCategory] = useState<string>('all')
  const filteredTemplates = templateCategory === 'all'
    ? TEMPLATES
    : TEMPLATES.filter((t) => t.category === templateCategory)
  // When the user has decisions, the templates section is collapsed by default
  // but they can expand it to seed another one (unblocks the Compare flow).
  const [showTemplates, setShowTemplates] = useState(false)
  const [warmSeed, setWarmSeed] = useState<WarmStartSeed | null>(null)

  return (
    <>
    <Helmet>
      <title>Dashboard — LifeOdds</title>
      <meta name="description" content="Your decisions, all in one place. Run simulations, review results, and track your best moves." />
    </Helmet>
    <div className="space-y-12" data-testid="dashboard-page">
      {/* Hero — only shown to new users with no decisions yet */}
      {decisions.length === 0 && (
        <section className="anim-rise relative">
          <div className="grid lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 chip border-primary text-primary-ink">
                <Dice size={14} weight="fill" /> Monte Carlo Decisions
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                Big decisions deserve <br />
                <span className="bg-primary text-white px-3 py-1 rounded-2xl inline-block transform -rotate-1 border-2 border-edge shadow-brutal-sm">
                  better dice.
                </span>
              </h1>
              <p className="text-lg text-ink-muted max-w-2xl font-medium">
                Stop pro/con-ing your way to regret. Model your options as probabilities,
                sample the future <span className="mono font-bold">1,000 times</span>, see which
                choice actually wins. <span className="text-xs uppercase tracking-widest text-ink-muted">(That's Monte Carlo — not luck.)</span>
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  data-testid="ai-intake-btn"
                  onClick={() => setShowIntake(true)}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Robot size={20} weight="fill" /> Build with AI
                </button>
                <button
                  data-testid="new-decision-btn"
                  onClick={() => setShowNew(true)}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  <Plus size={18} weight="bold" /> Start from scratch
                </button>
                <Link to="/agent" data-testid="open-agent-link" className="btn-secondary inline-flex items-center gap-2">
                  <Sparkle size={18} weight="fill" /> AI agent
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Compact header for returning users */}
      {decisions.length > 0 && (
        <div className="anim-rise flex items-center justify-between gap-4 flex-wrap">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Your decisions</h1>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="ai-intake-btn"
              onClick={() => setShowIntake(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Robot size={18} weight="fill" /> Build with AI
            </button>
            <button
              data-testid="new-decision-btn"
              onClick={() => setShowNew(true)}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Plus size={18} weight="bold" /> New decision
            </button>
          </div>
        </div>
      )}

      {/* New-decision drawer */}
      {showNew && (
        <div className="card-brutal p-6 anim-pop" data-testid="new-decision-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-2xl font-bold">Name your decision</h3>
            <button onClick={() => setShowNew(false)} className="btn-ghost" data-testid="cancel-new-decision">
              Cancel
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="new-decision-name-input" className="label-bold mb-1 block">What are you deciding?</label>
              <input
                autoFocus
                id="new-decision-name-input" data-testid="new-decision-name-input"
                className="input-brutal w-full"
                placeholder="Should I take the new job in Berlin?"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && create()}
              />
            </div>
            <div>
              <label htmlFor="new-decision-horizon-select" className="label-bold mb-1 block">Horizon</label>
              <select
                id="new-decision-horizon-select" data-testid="new-decision-horizon-select"
                className="input-brutal w-full"
                value={draft.horizon}
                onChange={(e) => setDraft({ ...draft, horizon: parseInt(e.target.value) })}
              >
                {[1, 2, 3, 5, 7, 10, 15, 20].map((y) => (
                  <option key={y} value={y}>
                    {y} year{y > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={create} data-testid="create-decision-btn" className="btn-primary inline-flex items-center gap-2">
              Start building <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Templates — full grid when no decisions yet, collapsible when user already has some */}
      {decisions.length === 0 ? (
        <section className="space-y-4" data-testid="templates-section">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-heading text-2xl font-bold">Or try a template</h2>
            <span className="label-bold">{TEMPLATES.length} pre-built decisions</span>
          </div>

          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2" data-testid="template-categories">
            {Object.entries(CATEGORY_LABELS).map(([key, info]) => {
              const active = templateCategory === key
              return (
                <button
                  key={key}
                  data-testid={`template-cat-${key}`}
                  onClick={() => setTemplateCategory(key)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 font-bold text-xs uppercase tracking-wider transition-all',
                    active
                      ? 'bg-primary text-white border-edge shadow-brutal-sm -translate-y-0.5'
                      : 'border-edge bg-surface hover:bg-muted',
                  )}
                >
                  <span aria-hidden>{info.emoji}</span> {info.label}
                </button>
              )
            })}
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredTemplates.map((t, i) => {
              const Icon = templateIcon(t.key)
              return (
                <button
                  key={t.key}
                  data-testid={`template-${t.key}-btn`}
                  onClick={() => useTemplate(t.key)}
                  className="anim-rise card-brutal card-brutal-hover p-5 text-left group flex flex-col"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <div className="w-11 h-11 rounded-xl border-2 border-edge bg-secondary/15 grid place-items-center mb-3 group-hover:rotate-[-6deg] transition-transform">
                    <Icon size={22} weight="duotone" className="text-secondary-ink" />
                  </div>
                  <div className="text-2xl mb-1">{t.emoji}</div>
                  <h3 className="font-heading text-lg font-bold leading-tight mb-1">{t.label}</h3>
                  <p className="text-sm text-ink-muted mb-3 flex-1">{t.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="chip border-edge text-ink-muted !text-[10px] !py-0.5 !px-2">
                      {CATEGORY_LABELS[t.category]?.label || t.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-primary-ink font-bold">
                      Use <ArrowRight size={12} weight="bold" />
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </section>
      ) : (
        <section className="space-y-3" data-testid="templates-section-collapsed">
          <button
            onClick={() => setShowTemplates((s) => !s)}
            data-testid="toggle-templates-btn"
            className="w-full card-brutal p-4 flex items-center justify-between gap-2 hover:bg-muted/30 transition-colors text-left"
          >
            <div>
              <div className="font-heading font-black">Browse {TEMPLATES.length} templates</div>
              <div className="text-xs text-ink-muted">
                Seed a second decision in one click — perfect for the Compare flow.
              </div>
            </div>
            <span
              className={cn(
                'chip border-primary text-primary-ink inline-flex transition-transform',
                showTemplates && 'rotate-180',
              )}
              aria-hidden
            >
              ▾
            </span>
          </button>
          {showTemplates && (
            <div className="space-y-3" data-testid="templates-expanded">
              <div className="flex flex-wrap gap-2" data-testid="template-categories">
                {Object.entries(CATEGORY_LABELS).map(([key, info]) => {
                  const active = templateCategory === key
                  return (
                    <button
                      key={key}
                      data-testid={`template-cat-${key}`}
                      onClick={() => setTemplateCategory(key)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 font-bold text-xs uppercase tracking-wider transition-all',
                        active
                          ? 'bg-primary text-white border-edge shadow-brutal-sm -translate-y-0.5'
                          : 'border-edge bg-surface hover:bg-muted',
                      )}
                    >
                      <span aria-hidden>{info.emoji}</span> {info.label}
                    </button>
                  )
                })}
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredTemplates.map((t) => {
                  const Icon = templateIcon(t.key)
                  return (
                    <button
                      key={t.key}
                      data-testid={`template-${t.key}-btn`}
                      onClick={() => useTemplate(t.key)}
                      className="card-brutal card-brutal-hover p-4 text-left group flex flex-col"
                    >
                      <div className="w-9 h-9 rounded-xl border-2 border-edge bg-secondary/15 grid place-items-center mb-2 group-hover:rotate-[-6deg] transition-transform">
                        <Icon size={18} weight="duotone" className="text-secondary-ink" />
                      </div>
                      <h3 className="font-heading text-base font-bold leading-tight mb-0.5">
                        {t.emoji} {t.label}
                      </h3>
                      <p className="text-xs text-ink-muted line-clamp-2 mb-2 flex-1">{t.description}</p>
                      <span className="inline-flex items-center gap-1 text-primary-ink font-bold text-xs">
                        Use <ArrowRight size={11} weight="bold" />
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </section>
      )}

      <CheckinsSection />

      {/* Decisions list */}
      {decisions.length > 0 && (
        <section className="anim-rise space-y-4" style={{ animationDelay: '60ms' }}>
          <StreaksCard />
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <h2 className="font-heading text-2xl font-bold">Your decisions</h2>
            <div className="flex items-center gap-3">
              {decisions.filter((d) => results[d.id]).length >= 2 && (
                <Link
                  to="/compare"
                  data-testid="open-compare-link"
                  className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                >
                  <Scales size={14} weight="fill" /> Compare two
                </Link>
              )}
              <span className="label-bold">{decisions.length} active</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {decisions.map((d, i) => {
              const lastResult = results[d.id]
              const ageDays = lastResult ? daysAgo(lastResult.created_at) : null
              const isStale = ageDays !== null && ageDays >= REROLL_AGE_DAYS
              return (
                <div
                  key={d.id}
                  className={cn('anim-rise card-brutal card-brutal-hover p-5 flex flex-col')}
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`decision-card-${d.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-heading text-lg font-bold leading-tight">{d.name}</h3>
                    {deletingId === d.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { removeDecision(d.id); setDeletingId(null) }}
                          data-testid={`delete-confirm-${d.id}`}
                          className="text-xs font-bold text-primary-ink px-2 py-1 rounded border-2 border-primary hover:bg-primary/10"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => setDeletingId(null)}
                          data-testid={`delete-cancel-${d.id}`}
                          className="text-xs font-bold px-2 py-1 rounded border-2 border-edge hover:bg-muted"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeletingId(d.id)}
                        aria-label={`Delete ${d.name}`}
                        data-testid={`delete-decision-${d.id}`}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-ink-muted hover:text-primary-ink transition-colors"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="chip">{d.horizon_years}y horizon</span>
                    <span className="chip">{d.choices.length} option{d.choices.length !== 1 ? 's' : ''}</span>
                    {isStale && (
                      <span
                        className="chip border-primary text-primary-ink bg-primary/10"
                        data-testid={`stale-chip-${d.id}`}
                      >
                        🔁 {ageDays}d old
                      </span>
                    )}
                  </div>

                  {isStale && (
                    <div
                      className="mb-4 rounded-xl border-2 border-primary bg-primary/5 p-3 text-sm leading-snug"
                      data-testid={`reroll-banner-${d.id}`}
                    >
                      <div className="font-heading font-black mb-0.5">
                        🔁 You ran this {ageDays} days ago
                      </div>
                      <p className="text-ink-muted">
                        Re-roll with updated numbers — Bayesian-update any variable with what you've learned since.
                      </p>
                      {isStale && <RerollHint decision={d} ageDays={ageDays!} />}
                    </div>
                  )}

                  <div className="mono text-xs text-ink-muted mb-4">
                    {lastResult ? (
                      <>Last simulated {new Date(lastResult.created_at).toLocaleDateString()}</>
                    ) : (
                      <>Updated {new Date(d.updated_at).toLocaleDateString()}</>
                    )}
                  </div>

                  <div className="mt-auto flex gap-2">
                    {isStale ? (
                      <Link
                        to={`/decision/${d.id}`}
                        data-testid={`reroll-decision-${d.id}`}
                        className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5"
                      >
                        <ArrowsClockwise size={16} weight="bold" /> Re-roll
                      </Link>
                    ) : (
                      <Link
                        to={`/decision/${d.id}`}
                        data-testid={`open-decision-${d.id}`}
                        className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5"
                      >
                        Open <ArrowRight size={16} weight="bold" />
                      </Link>
                    )}
                  </div>
                </div>
              )
            })}
            {/* Add new card */}
            <button
              onClick={() => setShowNew(true)}
              data-testid="add-decision-card-btn"
              className="rounded-2xl border-2 border-dashed border-edge p-5 flex flex-col items-center justify-center gap-2 text-ink-muted hover:text-primary-ink hover:border-primary hover:bg-primary/5 transition-all min-h-[200px]"
            >
              <Plus size={32} weight="bold" />
              <span className="font-bold">Add another decision</span>
            </button>
          </div>
        </section>
      )}

      <DecisionIntakeChat open={showIntake} seed={intakeSeed} onClose={() => { setShowIntake(false); setIntakeSeed(null) }} />

      <WarmStartModal seed={warmSeed} source="dashboard" onClose={() => setWarmSeed(null)} />

      {/* Guided tour welcome card — only for first-time users with no decisions */}
      {showTourLauncher && <TourLauncher />}
    </div>
    </>
  )
}
